/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';
import { Point, utils } from 'pixi.js-legacy';
import {
	LevelOfDetail,
	LEVEL_OF_DETAIL_SIGNALS,
	LEVEL_OF_DETAIL_TABLE,
} from '@appPlanApplication/lod/LevelOfDetail';
import { distinctUntilChanged, filter, sample, switchMap, take, takeUntil } from 'rxjs/operators';
import { FancyTicketAction } from 'src/app/app-data/actions/ticket-actions';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { FancySpritePool } from 'src/app/app-data/models/data-types/GenericPool';
import { Side, Ticket, TicketRenderTypes } from '@touchplan/common';
import { pinColors, pinState, PromisePin, PromisePinData } from '@touchplan/common';
import { planRendererInfo } from '@appCanvasCommon/core/RendererInfo';
import { Camera } from '@appCanvasCommon/core/Camera';
import { FancyText } from '@appCanvasCommon/fontpath/FancyText';
import { ITriangulateData } from '@appCanvasCommon/fontpath/fontpath.interfaces';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { TICKET_TEXTURE_OPTIONS } from '../tickets/TICKET_TEXTURE_OPTIONS';
import { Pin } from './Pin';
import { PinnedCount } from './PinnedCount';
import { convertHexStringToNumber } from 'utils/color-utils';
import { TicketType } from '@touchplan/common';
import { logger } from '@touchplan/logging';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { TicketSelection } from '../../../app-data/models/TicketSelection';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { GRANULAR_VIEW_SCALE_THRESHOLD } from '@appPlanApplication/tickets/TicketContainer';

/**
 * An enum used to control FancyPinHistory drawing options
 */
export enum FANCY_PIN_OPTIONS {
	// The line width
	LINE_WIDTH = 1,
	// The strength of the curve. The number represents the Y offset applied for the quadratic bezier curve control point
	CURVE_STRENGTH = 44,
	// Flag width (The blue or red flag that is drawn for the actual finish position)
	FLAG_STATUS_WIDTH = 10,
	// The width of the pin sprite
	PIN_SPRITE_WIDTH = 34,
	// The height of the pin sprite
	PIN_SPRITE_HEIGHT = 34,
	// The width of the pin sprite when not the latest pin
	LATEST_PIN_SPRITE_WIDTH = 49,
	// The height of the pin sprite when not the latest pin
	LATEST_PIN_SPRITE_HEIGHT = 49,
	// The offset applied to the pin counter sprite to position it correctly
	PIN_COUNTER_X_OFFSET = 12,
	// The offset applied to the pin counter sprite to position it correctly
	PIN_COUNTER_Y_OFFSET = -2,
}

interface PromisePinDataWithPosition extends Partial<PromisePinData> {
	left: number;
	top: number;
	side: Side;
	width: number;
	height: number;
	livePromisePeriodStack: any;
}

interface IPinHistoryPinCache {
	[key: string]: PromisePinDataWithPosition;
}

/**
 * Interface to define our dirty cache
 */
interface IPinHistoryDirtyCache {
	visible: boolean;
	oldPinData: IPinHistoryPinCache;
	lastSide: Side;
}

/**
 * The FancyPinHistory class is responsible for drawing all Pins and Pin Breadcrumbs for all tickets.
 *
 * It listens to the planState.tickets.list$ observable and does a re-draw when ticket data changes.
 */
export class FancyPinHistory {
	public renderable = false;
	private _deferredTickets;
	private _pinPool;
	private _pinnedCountPool;
	private shutdownSubject$: Subject<boolean> = new Subject();
	private _camera: Camera;
	public lodLevel = LEVEL_OF_DETAIL_TABLE.LOW;
	/**
	 * An array used to track DisplayObjects that need to be cleaned up when the FancyPinHistory class is destroyed
	 */
	private _destroyList = [];
	/**
	 * An instance to the PIXI.Graphics class which draws the dashed lines
	 */
	private _graphics: FancyGraphics;
	/**
	 * An instance of a PIXI.Container to store the graphics object.
	 */
	private _pinLineContainer: FancyContainer = new FancyContainer();
	private _pinCountContainer: FancyContainer = new FancyContainer();

	/**
	 * A cache to track the tickets which are part of our draw list
	 */
	private drawList: Map<string, IPinHistoryDirtyCache> = new Map();
	/**
	 * Used to control if the pin history is dirty or not. dirty = true means we need to re-draw the stencil buffer and re-upload to the gpu.
	 *
	 * NOTE: Pins are updated on every ticket change. This flag is specifically used to control when we update the dashed lines.
	 */
	private dirty = true;
	/**
	 * Used to enable solid lines. Renders much faster than dashed lines.
	 */
	private enableSolidLines = false;

	private getCachedNumber;
	private ticketEditSync: Map<string, Ticket>;

	/**
	 * Used to enable camera culling. If true, pins, pinlines, and vertical lines will only render if they're in the camera view.
	 *
	 * NOTE: Enabling this on WebGL can cause performance issues when zooming and panning the canvas, as the camera culling will hide and
	 * show sections of the pin history, requiring the graphics object to be redrawn and hence, updating the batch.
	 */
	private enableCameraCulling: boolean;
	private enableSolidLines$ = this.userPreferenceStateService.enableSolidLines$;

	constructor(
		public canvasAngularLand: CanvasAngularLand,
		camera: Camera,
		render: () => any,
		private userPreferenceStateService: UserPreferenceStateService
	) {
		// Set the parent group of our pinLineContainer to be UNDER the tickets. This ensures that our lines will always render below tickets
		// regardless of the order they are added into the scene graph
		this._graphics = new FancyGraphics();
		this._graphics.alpha = 0.7;

		this._camera = camera;

		// Enable camera culling only when in canvas. In WebGL it's more expensive to redraw the graphics everytime we pan
		// and zoom because changing pin history visibility will require an update to the graphics batch data.
		this.enableCameraCulling = !planRendererInfo.isWebGL();

		// Add the graphics object as a child to the pinLineContainer and then add our pinLineContainer to the world (cameraContainer)
		this._pinLineContainer.addChild(this._graphics);

		camera.cameraContainer.addChild(this._pinLineContainer, this._pinCountContainer);

		// Create our FancySpritePool for TicketPins
		const pinCreator = (): Pin => {
			return new Pin(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_PINNED_BACKGROUND_NAME]);
		};

		const pinnedCountCreator = (): PinnedCount => {
			return new PinnedCount(utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_PINNED_BACKGROUND_NAME]);
		};

		const pinPool: FancySpritePool = new FancySpritePool(pinCreator, this._pinLineContainer);
		const pinnedCountPool: FancySpritePool = new FancySpritePool(pinnedCountCreator, this._pinCountContainer);

		this._pinPool = pinPool;
		this._pinnedCountPool = pinnedCountPool;

		// Watch for ticket changes
		canvasAngularLand.tickets.list$
			.pipe(takeUntil(this.shutdownSubject$), sample(canvasAngularLand.renderQueueWithCleanup$))
			.subscribe((allTickets: Map<string, Ticket>) => {
				this.drawList.clear();
				this._deferredTickets = allTickets;
				this.update();
				render();
			});

		canvasAngularLand.tickets.editTicket$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((editTickets) => (this.ticketEditSync = editTickets));

		// Handle showing/hiding of pin history by forcing the dirty flag to true and re-drawing
		this.enableSolidLines$
			.pipe(sample(canvasAngularLand.renderQueueWithCleanup$), takeUntil(this.shutdownSubject$))
			.subscribe((enableSolidLines) => {
				this.enableSolidLines = enableSolidLines;
				this.dirty = true;
				this.update();
				render();
			});

		// This observer only fires for selected tickets that are dragged.
		// The purpose of this one is to re-draw each tick when you are dragging a ticket (So dashed lines update)
		canvasAngularLand.tickets.selectedTicket$
			.pipe(
				takeUntil(this.shutdownSubject$),
				switchMap((res: TicketSelection) => {
					return canvasAngularLand.tickets.soakedRaw$.pipe(
						filter((action) => res.list.has(action.key)),
						filter((action) => action.payload.renderType === TicketRenderTypes.drag),
						filter((action) => !!action.payload.view.liveLastPromise)
					);
				}),
				sample(canvasAngularLand.renderQueueWithCleanup$)
			)
			.subscribe((ticketAction: FancyTicketAction) => {
				if (!this.renderable) {
					return;
				}
				this.dirty = true;
			});

		// Remove item from drawList when ticket removed
		canvasAngularLand.tickets.soakedRaw$
			.pipe(takeUntil(this.shutdownSubject$), sample(canvasAngularLand.renderQueueWithCleanup$))
			.subscribe((action) => {
				if (canvasAngularLand.tickets.isRemoveAction(action)) {
					this.drawList.delete(action.key);
					this.dirty = true;
					this.update();
				}
			});

		// Handle transparency of pins
		canvasAngularLand.filterTransparencyValue$
			.pipe(distinctUntilChanged(), takeUntil(this.shutdownSubject$))
			.subscribe((filterTransparencyValue) => {
				if (typeof filterTransparencyValue === 'number') {
					this._pinLineContainer.alpha = filterTransparencyValue;
					this._pinCountContainer.alpha = filterTransparencyValue;
					this._pinLineContainer.eventMode = EventMode.None;
				} else {
					this._pinLineContainer.alpha = 1;
					this._pinCountContainer.alpha = 1;
					this._pinLineContainer.eventMode = EventMode.Static;
				}
			});

		this.getCachedNumber = (function () {
			const cache: Map<string, ITriangulateData> = new Map();
			return function (key: string, creatorFn): ITriangulateData {
				const res = cache.get(key);
				if (res) {
					return res;
				} else if (creatorFn) {
					const newData: ITriangulateData = creatorFn();
					cache.set(key, newData);
					return newData;
				}
			};
		})();

		LevelOfDetail.emitter.on(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);

		// Mark our container and graphics as an object that needs to be destroyed
		this._destroyList.push(this._pinLineContainer, this._pinCountContainer, pinPool, pinnedCountPool);
	}

	public lodChanged = (newLod, oldLod) => {
		this.renderable = this.lodLevel <= newLod || LevelOfDetail.isForced;
		if (this.renderable) {
			this._graphics.renderable = true;
			this._pinPool.show();
			this._pinnedCountPool.show();
			this.update();
		} else if (!this.renderable) {
			this._graphics.renderable = false;
			this._pinPool.hide();
			this._pinnedCountPool.hide();
		}
	};

	public update() {
		if (this.renderable && this._deferredTickets) {
			this._updateDrawList(this._deferredTickets);
			this._draw(this.drawList, this._pinPool, this._pinnedCountPool);
		}
	}

	private _updateDrawList(tickets: Map<string, Ticket>): Map<string, IPinHistoryDirtyCache> {
		if (tickets.size !== this.drawList.size) {
			this.dirty = true;
		}
		tickets.forEach((ticket: Ticket) => {
			// MOC-2953 - Only draw if ticket no hidden
			if (ticket.hidden) {
				return;
			}
			let pinVisible = false;
			let verticalBarsVisible = false;
			let completedDashedLineVisible = false;
			let dashedLineVisible = false;
			// Always draw the latest pin, if exists and visible...
			if (ticket.view.liveLastPromise) {
				const latestPromisePin: PromisePin = ticket.view.liveLastPromise;
				pinVisible = this.isPinVisible(latestPromisePin.data, ticket);

				// Check if the latest pin was finished and the actualFinish are not equal. Draw the vertical bars if so.
				if (
					(latestPromisePin.data && latestPromisePin.data.state === pinState.completedEarly) ||
					latestPromisePin.data.state === pinState.completedLate
				) {
					verticalBarsVisible = this.isVerticalBarsVisible(ticket);
				}

				// Draw from right edge of ticket to latest prior miss
				if (ticket.view.liveLastPromise.data.finishX !== ticket.view.left + ticket.view.width) {
					// Calculate the start point (The right edge of the ticket)
					const ticketStartPointRightEdge: Point = this._getPoint(
						ticket.view.left + ticket.view.width,
						ticket.view.top
					);
					// Calculate the end point (The latest promise pin)
					const ticketEndPoint: Point = this._getPoint(latestPromisePin.data.finishX, ticket.view.top);

					completedDashedLineVisible = this.isDashedLineVisible(ticketStartPointRightEdge, ticketEndPoint);
				}

				// Draw the dashed lines between prior misses
				let previousPromisePin: PromisePin;
				ticket.view.livePromisePeriodStack.forEach((promisePin: PromisePin) => {
					if (previousPromisePin && this.userPreferenceStateService.showPinHistory) {
						// Get the pin data
						const previousPinData: PromisePinData = previousPromisePin.data;
						const currentPinData: PromisePinData = promisePin.data;

						// Calculate the line start point.
						const dashStartPoint: Point = this._getPoint(currentPinData.finishX, ticket.view.top);

						// Calculate the line end point.
						const dashEndPoint: Point = this._getPoint(previousPinData.finishX, ticket.view.top);

						const result = this.isDashedLineVisible(dashStartPoint, dashEndPoint);

						if (result) {
							dashedLineVisible = true;
						}
					}
					previousPromisePin = promisePin;
				});
			}
			const visible: boolean =
				pinVisible || verticalBarsVisible || dashedLineVisible || completedDashedLineVisible;
			this._checkDirty(ticket, visible);
			this._updatePinCache(ticket, visible);
		});

		return this.drawList;
	}

	private _updatePinCache(ticket: Ticket, visible: boolean): void {
		const currentPromisePins: PromisePin[] = ticket.view.livePromisePeriodStack || [];

		// start mildly more efficient block
		if (!currentPromisePins.length) {
			// the _checkDirty assumes that nothing in oldPinData means dirty
			// so just... set it to empty
			if (!this.drawList.has(ticket.$id)) {
				this.drawList.set(ticket.$id, { visible, oldPinData: {}, lastSide: ticket.view.side });
			}
			return;
		}
		const oldPinData = this.drawList.get(ticket.$id)?.oldPinData || {};
		// end mildly more efficient block
		// const oldPinData = {}; // will also do the job, but it creates more garbage

		currentPromisePins.forEach((promisePin: PromisePin) => {
			const pinData: PromisePinData = promisePin.data;

			oldPinData[promisePin.$id] = {
				plannedStart: pinData.plannedStart,
				plannedFinish: pinData.plannedFinish,
				actualFinish: pinData.actualFinish,
				finishX: pinData.finishX,
				color: pinData.color,
				state: pinData.state,
				variance: pinData.variance,
				left: ticket.view.left,
				top: ticket.view.top,
				side: ticket.view.side,
				width: ticket.view.width,
				height: ticket.view.height,
				livePromisePeriodStack: ticket.view.livePromisePeriodStack,
			};
		});

		this.drawList.set(ticket.$id, { visible, oldPinData, lastSide: ticket.view.side });
	}

	private _checkDirty(ticket: Ticket, visible: boolean): void {
		const pinCache: IPinHistoryDirtyCache = this.drawList.get(ticket.$id);
		const oldData: IPinHistoryDirtyCache = this.drawList.get(ticket.$id);

		// We do not have a cache for this ticket. That means ticket is either new or it has been deleted. In either case, we need to refresh
		if (!oldData || !oldData.oldPinData || this.shouldScale()) {
			this.dirty = true;
			return;
		}

		const newData: PromisePin[] = ticket.view.livePromisePeriodStack || [];
		const pinsCache: IPinHistoryPinCache = oldData.oldPinData;

		const lastVisible: boolean = pinCache ? pinCache.visible : null;
		// If this ticket is currently visible, and it was NOT visible during the last tick, then that tells us this is a new ticket in the viewport.
		// If so, do an additional check to see if the new ticket actually has pins. If it does, then refresh the buffer.
		if (visible !== lastVisible && newData.length > 0) {
			this.dirty = true;
			return;
		}

		if (ticket.view.side !== pinCache.lastSide) {
			this.dirty = true;
			return;
		}

		newData.forEach((currentPin: PromisePin) => {
			const stalePin: PromisePinDataWithPosition = pinsCache[currentPin.$id];
			const currentPinData: PromisePinData = currentPin.data;
			// We don't have a copy of this pin in the pin cache. Mark the system as dirty so it refreshes.
			if (!stalePin) {
				this.dirty = true;
				return;
			}
			// Perform dirty check
			if (
				currentPinData.actualFinish !== stalePin.actualFinish ||
				stalePin.plannedStart !== currentPinData.plannedStart ||
				stalePin.plannedFinish !== currentPinData.plannedFinish ||
				stalePin.finishX !== currentPinData.finishX ||
				stalePin.color !== currentPinData.color ||
				stalePin.variance !== currentPinData.variance ||
				ticket.view.left !== stalePin.left ||
				ticket.view.top !== stalePin.top ||
				currentPinData.state !== stalePin.state ||
				ticket.view.livePromisePeriodStack !== stalePin.livePromisePeriodStack
			) {
				this.dirty = true;
			}
		});
	}

	/**
	 * Draws the promise pins and dashed lines for all tickets
	 * @param {Map<string, boolean>} drawList The map of ticket IDs to draw
	 * @param {FancySpritePool} pinPool Pool of {Pin} instances
	 * @param {FancySpritePool} pinnedCountPool Pool of {PinnedCount} instances
	 */
	private _draw(
		drawList: Map<string, IPinHistoryDirtyCache>,
		pinPool: FancySpritePool,
		pinnedCountPool: FancySpritePool
	): void {
		// Clear the stencil buffer and sprite pool
		if (this.dirty) {
			this._graphics.clear();
		}
		pinPool.reset();
		pinnedCountPool.reset();

		if (!this.renderable || !drawList || !this._deferredTickets) {
			return;
		}

		drawList.forEach((ticketCacheData: IPinHistoryDirtyCache, id: string) => {
			// Only process if this ticket is in our draw list.
			if (!ticketCacheData || !ticketCacheData.visible) {
				return;
			}

			const ticket: Ticket = this._deferredTickets.get(id);

			// MOC-2953 - Only draw if ticket no hidden
			if (!ticket || ticket.hidden || ticket.view.side === Side.PULL) {
				return;
			}

			// Always draw the latest pin, if exists
			if (ticket.view.liveLastPromise) {
				const latestPromisePin: PromisePin = ticket.view.liveLastPromise;
				this._drawPin(latestPromisePin.data, ticket, pinPool, true);

				// Only draw pinned count if there are prior misses
				if (ticket.view.livePromisePeriodStack.length - 1 > 0) {
					this._drawPinnedCount(latestPromisePin.data, ticket, pinnedCountPool);
				}

				// Check if the latest pin was finished and the actualFinish are not equal. Draw the vertical bars if so.
				if (
					this.dirty &&
					latestPromisePin.data &&
					(latestPromisePin.data.state === pinState.completedEarly ||
						latestPromisePin.data.state === pinState.completedLate)
				) {
					this._drawVerticalBars(latestPromisePin.data, ticket);
				}

				// Draw from right edge of ticket to latest prior miss
				if (this.dirty && ticket.view.liveLastPromise.data.finishX !== ticket.view.left + ticket.view.width) {
					// Calculate the start point (The right edge of the ticket)
					const ticketStartPointRightEdge: Point = this._getPoint(
						ticket.view.left + ticket.view.width,
						ticket.view.top
					);
					// Calculate the end point (The latest promise pin)
					const ticketEndPoint: Point = this._getPoint(latestPromisePin.data.finishX, ticket.view.top);
					// Use the Midpoint Formula to determine the Quadratic Curve control point. For the Y, use the CURVE_STRENGTH offset
					const ticketControlPoint: Point = this._getPoint(
						(ticketEndPoint.x + ticketStartPointRightEdge.x) / 2,
						ticket.view.top - FANCY_PIN_OPTIONS.CURVE_STRENGTH
					);

					this._drawDashedLine(
						ticketStartPointRightEdge,
						ticketControlPoint,
						ticketEndPoint,
						latestPromisePin.data.color
					);
				}

				// Draw the dashed lines between prior misses
				let previousPromisePin: PromisePin;
				ticket.view.livePromisePeriodStack.forEach((promisePin: PromisePin) => {
					if (
						previousPromisePin &&
						(this.userPreferenceStateService.showPinHistory || this.ticketEditSync.get(ticket.$id))
					) {
						// Get the pin data
						const previousPinData: PromisePinData = previousPromisePin.data;
						const currentPinData: PromisePinData = promisePin.data;

						// Draw a pin
						this._drawPin(previousPinData, ticket, pinPool, false);

						if (this.dirty) {
							const offset = this._pinOffsetByType(TicketUtils.getTicketType(ticket));
							// Calculate the line start point.
							const dashStartPoint: Point = this._getPoint(
								currentPinData.finishX + offset.x,
								ticket.view.top + offset.y
							);

							// Calculate the line end point.
							const dashEndPoint: Point = this._getPoint(
								previousPinData.finishX + offset.x,
								ticket.view.top + offset.y
							);

							// Use the Midpoint Formula to determine the Quadratic Curve control point. For the Y, use the CURVE_STRENGTH offset
							const dashControlPoint: Point = this._getPoint(
								(dashEndPoint.x + dashStartPoint.x) / 2 + offset.x,
								ticket.view.top - FANCY_PIN_OPTIONS.CURVE_STRENGTH + offset.y
							);

							this._drawDashedLine(
								dashStartPoint,
								dashControlPoint,
								dashEndPoint,
								previousPromisePin.data.color
							);
						}
					}
					previousPromisePin = promisePin;
				});
			}
		});

		// We are no longer dirty.
		this.dirty = false;
	}

	private isPinVisible(promisePin: PromisePinData, ticket: Ticket): boolean {
		return !this.enableCameraCulling || this._camera.globalView.contains(promisePin.finishX, ticket.view.top);
	}

	private isVerticalBarsVisible(ticket: Ticket): boolean {
		return (
			!this.enableCameraCulling ||
			this._camera.globalView.contains(ticket.view.left + ticket.view.width, ticket.view.top) ||
			this._camera.globalView.contains(
				ticket.view.liveFinishX - FANCY_PIN_OPTIONS.FLAG_STATUS_WIDTH,
				ticket.view.top
			)
		);
	}

	private isDashedLineVisible(firstPoint: Point, secondPoint: Point): boolean {
		const screenPoint: Point = new Point(
			this._camera.realToScreenX(secondPoint.x),
			this._camera.realToScreenY(secondPoint.y)
		);
		const w: number = Math.abs(firstPoint.x - secondPoint.x);
		return (
			!this.enableCameraCulling ||
			!(
				screenPoint.x + w * this._camera.scale.x < 0 ||
				screenPoint.x >= this._camera.view.width ||
				screenPoint.y + FANCY_PIN_OPTIONS.CURVE_STRENGTH * this._camera.scale.y < 0 ||
				screenPoint.y >= this._camera.view.height
			)
		);
		//return (this._camera.globalView.contains(firstPoint.x, firstPoint.y) || this._camera.globalView.contains(secondPoint.x, secondPoint.y));
	}

	private shouldScale(): boolean {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7431
		// This is a temporary feature flag to disable the maximum zoom level in GTP.
		// Using sincronous flag check for convenience since this will remove soon.
		const isMaxZoomEnabled = this.canvasAngularLand.featureFlagManager.getFlag(
			DynamicFlagTypes.GTP_ENABLE_MAXIMUM_ZOOM
		);

		if (!isMaxZoomEnabled) {
			return false;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7431
		return this._camera.scale.x > GRANULAR_VIEW_SCALE_THRESHOLD;
	}

	/**
	 * Draws the pin on the canvas
	 * @param {PromisePinData} promisePin
	 * @param {Ticket} ticket
	 * @param {FancySpritePool} pinPool
	 * @param {Boolean} latest Is this the most recent pin? If it is, draw larger than the rest.
	 */
	private _drawPin(
		promisePin: PromisePinData,
		ticket: Ticket,
		pinPool: FancySpritePool,
		latest: boolean = false
	): void {
		// Calculate the line end point.
		const pinPoint: Point = this._getPoint(promisePin.finishX, ticket.view.top);

		const pinSprite: FancySprite = pinPool.grab() as FancySprite;
		if (!pinSprite) {
			return;
		}

		if (latest) {
			pinSprite.width = this.shouldScale()
				? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.LATEST_PIN_SPRITE_WIDTH)
				: FANCY_PIN_OPTIONS.LATEST_PIN_SPRITE_WIDTH;
			pinSprite.height = this.shouldScale()
				? this._camera.scaleToWorldY(FANCY_PIN_OPTIONS.LATEST_PIN_SPRITE_HEIGHT)
				: FANCY_PIN_OPTIONS.LATEST_PIN_SPRITE_HEIGHT;
		} else {
			pinSprite.width = this.shouldScale()
				? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.PIN_SPRITE_WIDTH)
				: FANCY_PIN_OPTIONS.PIN_SPRITE_WIDTH;
			pinSprite.height = this.shouldScale()
				? this._camera.scaleToWorldY(FANCY_PIN_OPTIONS.PIN_SPRITE_HEIGHT)
				: FANCY_PIN_OPTIONS.PIN_SPRITE_HEIGHT;
		}
		const offset = this._pinOffsetByType(TicketUtils.getTicketType(ticket));
		pinSprite.x = pinPoint.x - pinSprite.width / 2 + offset.x;
		pinSprite.y = pinPoint.y - pinSprite.height / 2 + offset.y;
		if (promisePin.variance) {
			(pinSprite as Pin).variance = promisePin.variance.varianceReason;
		} else {
			(pinSprite as Pin).variance = null;
		}
		pinSprite.tint = convertHexStringToNumber(promisePin.color);
	}
	private _pinOffsetByType(type: string): any {
		const offset = { x: 0, y: 0 };
		switch (type) {
			case TicketType.Constraint:
				break;
			case TicketType.Milestone:
				offset.x = -16;
				offset.y = 16;
				break;
			default:
				break;
		}
		return offset;
	}
	/**
	 * Draws the prior missed count on the canvas
	 * @param {PromisePinData} promisePin
	 * @param {Ticket} ticket
	 * @param {FancySpritePool} pinnedCountPool Pool of {PinnedCount} instances
	 */
	private _drawPinnedCount(
		promisePin: PromisePinData,
		ticket: Ticket,
		pinnedCountPool: FancySpritePool
	): void {
		// Calculate the line end point.
		const pinPoint: Point = this._getPoint(promisePin.finishX, ticket.view.top);

		const baseCount: number = ticket.view.livePromisePeriodStack.length - 1;
		const count: string = baseCount > 99 ? '99' : baseCount + '';
		const pinnedCountSprite: PinnedCount = pinnedCountPool.grab() as PinnedCount;
		const fancyText = pinnedCountSprite.fancyText;
		const offset = this._pinOffsetByType(TicketUtils.getTicketType(ticket));
		pinnedCountSprite.width = this.shouldScale()
			? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.PIN_SPRITE_WIDTH)
			: FANCY_PIN_OPTIONS.PIN_SPRITE_WIDTH;
		pinnedCountSprite.height = this.shouldScale()
			? this._camera.scaleToWorldY(FANCY_PIN_OPTIONS.PIN_SPRITE_HEIGHT)
			: FANCY_PIN_OPTIONS.PIN_SPRITE_HEIGHT;

		const totalXOffset = FANCY_PIN_OPTIONS.PIN_COUNTER_X_OFFSET + offset.x;
		const totalYOffset = FANCY_PIN_OPTIONS.PIN_COUNTER_Y_OFFSET + offset.y;

		const scaledXOffset = this.shouldScale() ? this._camera.scaleToWorldX(totalXOffset) : totalXOffset;
		const scaledYOffset = this.shouldScale() ? this._camera.scaleToWorldY(totalYOffset) : totalYOffset;

		pinnedCountSprite.x = pinPoint.x - pinnedCountSprite.width / 2 + scaledXOffset;
		pinnedCountSprite.y = pinPoint.y + pinnedCountSprite.height / 2 + scaledYOffset;
		pinnedCountSprite.fancyText.text = count;
		// Create a cache of numbers so we don't have to keep calculating text
		if (pinnedCountSprite.fancyText.isFancy && pinnedCountSprite.fancyText.dirty) {
			try {
				const data: ITriangulateData = this.getCachedNumber(count, () =>
					pinnedCountSprite.fancyText.calculateText(true)
				);
				(<FancyText>pinnedCountSprite.fancyText).geometry.getIndex().update(data.indexData);
				(<FancyText>pinnedCountSprite.fancyText).verticesBuffer.update(data.vertexData);
			} catch {
				logger.error('Error creating cache of pinned count');
			}
			// We are manually handling text state. Remove dirty flag, but mark it as needing a new VAO for rendering.
			(<FancyText>pinnedCountSprite.fancyText).dirty = false;
		}
		pinnedCountSprite.tint = convertHexStringToNumber(pinColors.red);

		// Text is long. Offset the x a tad.
		if (count[1]) {
			fancyText.x = 26;
		}
	}
	/**
	 * Draws the late/early vertical lines on the canvas
	 * @param {PromisePinData} promisePin
	 * @param {Ticket} ticket
	 */
	private _drawVerticalBars(promisePin: PromisePinData, ticket: Ticket): void {
		// The point representing the right edge of the ticket
		const ticketRightEdge: Point = this._getPoint(ticket.view.left + ticket.view.width, ticket.view.top);

		// The scaled width of the flag status
		// We need to scale this because the flag status width is used to offset the actual finish position
		// so it doesn't overlap the ticket
		const scaledFlagStatusWidth = this.shouldScale()
			? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.FLAG_STATUS_WIDTH)
			: FANCY_PIN_OPTIONS.FLAG_STATUS_WIDTH;

		// The point representing where the actual finish 'flag' is drawn
		const actualFinish: Point = this._getPoint(
			ticket.view.liveFinishX - scaledFlagStatusWidth,
			ticket.view.top
		);

		this._graphics.moveTo(ticketRightEdge.x, ticketRightEdge.y);

		// This check fixes a problem where we draw an extra line for the early/late flag at the same position
		if (actualFinish.x !== ticketRightEdge.x - scaledFlagStatusWidth) {
			// The control point to manage the curve
			const cp: Point = this._getPoint(
				(actualFinish.x + ticketRightEdge.x) / 2,
				ticket.view.top - FANCY_PIN_OPTIONS.CURVE_STRENGTH
			);
			this._drawDashedLine(ticketRightEdge, cp, actualFinish, promisePin.color);
		}

		this._graphics.lineStyle();
		this._graphics.beginFill(convertHexStringToNumber(promisePin.color));
		this._graphics.drawRect(
			actualFinish.x,
			actualFinish.y,
			scaledFlagStatusWidth,
			(this.shouldScale()
				? this._camera.scaleToWorldY(ticket.view.height as number)
				: ticket.view.height) as number
		);
		this._graphics.endFill();
	}
	/**
	 * Draws the dashed lines between prior misses
	 * @param {Point} firstPoint The first PromisePinData
	 * @param {Point} controlPoint The control point for the curve
	 * @param {Point} endPoint The second PromisePinData
	 * @param {string} lineColor The color of the dashed line
	 */
	private _drawDashedLine(firstPoint: Point, controlPoint: Point, endPoint: Point, lineColor: string): void {
		if (this.enableSolidLines) {
			this._graphics.lineStyle(
				this.shouldScale()
					? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.LINE_WIDTH)
					: FANCY_PIN_OPTIONS.LINE_WIDTH,
				convertHexStringToNumber(lineColor)
			);
		} else {
			this._graphics.lineTextureStyle({
				width: this.shouldScale()
					? this._camera.scaleToWorldX(FANCY_PIN_OPTIONS.LINE_WIDTH)
					: FANCY_PIN_OPTIONS.LINE_WIDTH,
				color: convertHexStringToNumber(lineColor),
				texture: utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_PIN_DASH_TEXTURE],
			});
		}
		this._graphics.moveTo(firstPoint.x, firstPoint.y);

		// Optimization: Use our own formula to calculate the curve points, as quadraticCurveTo tends
		// to create way too many points and will give performance problems on plans with bigger lines.
		// Control points y is always the same in our case.
		const cp1: Point = this._getPoint((firstPoint.x + controlPoint.x) / 2, controlPoint.y);
		const cp2: Point = this._getPoint((endPoint.x + controlPoint.x) / 2, controlPoint.y);
		this._graphics.bezierCurveToWithResolution(firstPoint, endPoint, cp1, cp2, 5);
	}

	/**
	 * Helper method to return a new PIXI.Point
	 * @param {number} x The X coordinate (in world space)
	 * @param {number} y The Y coordinate (in world space)
	 */
	private _getPoint(x: number, y: number): Point {
		return new Point(x, y);
	}

	/**
	 * Called when the renderer is to be destroyed. This removes all elements in the _destroyList
	 * @param destroyOptions
	 */
	public destroy(destroyOptions?): void {
		this.shutdownSubject$.next(true);
		this._destroyList.forEach((thing) => (thing.destroy ? thing.destroy(destroyOptions) : null));
		this._destroyList = null;
		LevelOfDetail.emitter.off(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
		this._deferredTickets = null;
		this.getCachedNumber = null;
	}
}
